import Vue from 'vue';
import VueRouter from 'vue-router';
import Account from '@/components/base/Account/AccountComponent';
import Login from '@/components/base/Account/LoginComponent';
import ForgotPassword from '@/components/base/Account/ForgotPasswordComponent';
import ResetPassword from '@/components/base/Account/ResetPasswordComponent';
import Home from '@/components/base/Home/HomeComponent';
import Landing from '@/components/base/Landing/LandingComponent';
import { Contracts, ContractEdit } from '@/components/crud/contract';
import { Vehicles } from '@/components/crud/vehicle';
import { SignOffEdit, SignOffSuccess } from '@/components/crud/sign-off';
import { Depots } from '@/components/crud/depot';
import About from '@/components/base/About/AboutComponent';
import News from '@/components/base/News/NewsComponent';
import Stats from '@/components/base/Stats/StatsComponent';
import { Users, UserEdit } from '@/components/crud/user';
import DefaultLayout from '@/components/layouts/DefaultLayout';
import * as pick from 'lodash/pick';
import * as map from 'lodash/map';
import ReturnLocations from "../components/base/ReturnLocations/ReturnLocations"
import DepotStatistics from "../components/base/Stats/DepotStatistics"

const qs = require('query-string');

Vue.use(VueRouter);

const GUEST_ROUTES = [
  {
    path: '/login',
    name: 'login',
    component: Login,
  },
  {
    path: '/register',
    name: 'register',
    component: Login,
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: ForgotPassword,
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: ResetPassword,
  },
  {
    path: '/sign-off/:uid',
    name: 'sign-off',
    component: SignOffEdit,
    meta: {
      apiPath: '/contract/:uid/sign-off',
    },
  },
  {
    path: '/sign-off/:uid/success',
    name: 'sign-off-success',
    component: SignOffSuccess
  },
  {
    path: '/return-locations',
    name: 'return-locations',
    component: ReturnLocations,
    meta: {
      apiPath: '/return-locations',
    }
  },
  {
    path: '/welcome',
    name: 'welcome',
    component: Landing,
    meta: {
      // apiPath: '/welcome',
    },
  },
]

const ADMIN_ROUTES = [
  {
    path: '/users',
    name: 'users',
    component: Users,
  },
  {
    path: '/users/create',
    name: 'user-create',
    component: UserEdit,
    meta: {
      apiPath: '/users/create',
    },
  },
  {
    path: 'depots/statistics',
    name: 'depot-statistics',
    component: DepotStatistics,
    meta: {
      apiPath: '/depots/statistics',
    }
  }
];

const API_USER_ROUTES = [
  {
    path: '/account',
    name: 'account',
    component: Account,
    meta: {
      apiPath: '/account',
    },
  },
]

const GUEST_ROUTE_NAMES = map(GUEST_ROUTES, 'name');
const API_USER_ROUTE_NAMES = map(API_USER_ROUTES, 'name');
const ADMIN_ROUTE_NAMES = map(ADMIN_ROUTES, 'name');

export default (store) => {
  const router = new VueRouter({
    mode: 'history',
    routes: [
      ...GUEST_ROUTES,
      {
        path: '/',
        component: DefaultLayout,
        children: [
          ...ADMIN_ROUTES,
          ...API_USER_ROUTES,
          {
            path: '/',
            name: 'home',
            component: Home,
            meta: {
              apiPath: '/home',
            },
          },
          {
            path: '/book',
            name: 'contract-create',
            component: ContractEdit,
            meta: {
              forwardQuery: ['vehicle_id', 'contract_template_id', 'client_id', 'depot_id'],
              apiPath: '/contracts/create',
            },
          },
          {
            path: '/contracts/:id/edit',
            name: 'contract-edit',
            component: ContractEdit,
            meta: {
              apiPath: '/contracts/:id/edit',
            },
          },
          {
            path: '/contracts',
            name: 'contracts',
            component: Contracts,
          },
          {
            path: '/vehicles',
            name: 'vehicles',
            component: Vehicles,
          },
          {
            path: '/depots',
            name: 'depots',
            component: Depots,
          },
          {
            path: '/news',
            name: 'news',
            component: News,
            meta: {
              apiPath: '/news',
            },
          },
          {
            path: '/stats',
            name: 'stats',
            component: Stats,
            meta: {
              apiPath: '/stats',
            },
          },
          {
            path: '/about',
            name: 'about',
            component: About
          }
        ]
      }
    ]
  })

  const isGuestRoute = route => GUEST_ROUTE_NAMES.indexOf(route.name) > -1;
  const isApiUserRoute = route => API_USER_ROUTE_NAMES.indexOf(route.name) > -1;
  const isAdminRoute = route => ADMIN_ROUTE_NAMES.indexOf(route.name) > -1;
  const isWelcome = route => (route.name === 'welcome');

  router.beforeEach((to, from, next) => {

    const isAuthenticated = store.getters.isAuthenticated;
    const hasFrontendAccess = store.getters.user?.has_frontend_access ?? false;
    const hasApiAccess = store.getters.user?.hasApiRole ?? false;

    if (to.path == '/' && !isAuthenticated) return next('/welcome');
    if (isAuthenticated && isGuestRoute(to) && isWelcome(to)) return next(store.getters.defaultRoute);
    if (isAuthenticated && !isApiUserRoute(to) && !hasFrontendAccess) return next(store.getters.defaultRoute);
    if (!isGuestRoute(to) && !isAuthenticated) return next('/login');
    if (isAdminRoute(to) && !store.getters.isAdmin) return next('/login');
    if (!to.meta.apiPath) return next();
    let apiPath = to.meta.apiPath;

    for (const param in to.params) {
      if (to.params.hasOwnProperty(param)) {
        const val = to.params[param];
        apiPath = apiPath.replace(':' + param, val);
      }
    }

    if (to.meta.forwardQuery) {
      if (to.meta.forwardQuery === true) {
        apiPath += '?' + qs.stringify(to.query);
      } else {
        const obj = pick(to.query, to.meta.forwardQuery);
        apiPath += '?' + qs.stringify(obj);
      }
    }

    axios.get(`/api${apiPath || to.path}`).then(({ data }) => {
      Object.assign(to.params, data);

      next();
    }).catch((e) => {
      const resp = e.response;
      if (resp.data?.message) {
        alert(resp.data.message);

        if (to.name === 'sign-off') {
          router.push({ name: 'welcome'});
          next();
        }
      }
    });
  })

  return router;
}
